/**
 * Represents the state of an async operation.
 * Used throughout the store for consistent loading/error handling.
 *
 * Note: data can be null in all states to work better with React's useState
 * which widens types. Use optional chaining when accessing data.
 */
export type TAsyncState<TData, TError = Error> =
    | { status: "idle"; data: null; error: null }
    | { status: "pending"; data: TData | null; error: null }
    | { status: "fulfilled"; data: TData | null; error: null }
    | { status: "rejected"; data: TData | null; error: TError };

/**
 * Initial state for async operations
 */
export const initialAsyncState = <TData, TError = Error>(): TAsyncState<TData, TError> => ({
    status: "idle",
    data: null,
    error: null,
});

/**
 * Create a pending state, optionally preserving existing data
 */
export const pendingAsyncState = <TData, TError = Error>(
    existingData: TData | null = null,
): TAsyncState<TData, TError> => ({
    status: "pending",
    data: existingData,
    error: null,
});

/**
 * Create a fulfilled state with data
 */
export const fulfilledAsyncState = <TData, TError = Error>(
    data: TData,
): TAsyncState<TData, TError> => ({
    status: "fulfilled",
    data,
    error: null,
});

/**
 * Create a rejected state with error, optionally preserving existing data
 */
export const rejectedAsyncState = <TData, TError = Error>(
    error: TError,
    existingData: TData | null = null,
): TAsyncState<TData, TError> => ({
    status: "rejected",
    data: existingData,
    error,
});

/**
 * Type guard to check if async state is fulfilled
 */
export const isFulfilled = <TData, TError>(
    state: TAsyncState<TData, TError>,
): state is TAsyncState<TData, TError> & { status: "fulfilled"; data: TData } => {
    return state.status === "fulfilled";
};

/**
 * Type guard to check if async state is pending
 */
export const isPending = <TData, TError>(
    state: TAsyncState<TData, TError>,
): state is TAsyncState<TData, TError> & { status: "pending" } => {
    return state.status === "pending";
};

/**
 * Type guard to check if async state is rejected
 */
export const isRejected = <TData, TError>(
    state: TAsyncState<TData, TError>,
): state is TAsyncState<TData, TError> & { status: "rejected"; error: TError } => {
    return state.status === "rejected";
};
